import { useState, type FormEvent } from "react";
import { Button, Field, FormDialog, DialogCancel } from "@cntrl/ui";
import { formErrorMessage } from "../../../app/hooks/apiError.js";
import { useAddFan } from "../../../app/hooks/useFans.js";
import { MetroHintNote } from "./MetroHintNote.js";
import styles from "./FanForm.module.scss";

/**
 * The add-fan modal — a manual CRM add. Never optimistic: explicit pending, server-confirm, verbatim
 * field errors (e.g. a duplicate email is a friendly message, not a toast). City is metro-rolled on save
 * the live hint below the city field previews it. A FormDialog: a Modal on desktop, a bottom Sheet on a phone (so the
 * soft keyboard never covers the form).
 */
export function AddFanModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const add = useAddFan();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [emailSubscribed, setEmailSubscribed] = useState(true);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    add.mutate(
      {
        firstName: firstName || null,
        lastName: lastName || null,
        email: email || null,
        phone: phone || null,
        city: city || null,
        state: state || null,
        country: country || null,
        showsAttended: 0,
        emailSubscribed,
        smsOptIn,
      },
      {
        onSuccess: onAdded,
        onError: (err) => setError(formErrorMessage(err, "Couldn't add the fan")),
      },
    );
  }

  return (
    <FormDialog
      title="Add fan"
      size="md"
      onClose={onClose}
      bodyAs="form"
      onSubmit={submit}
      footer={
        <>
          <DialogCancel />
          <Button type="submit" variant="primary" loading={add.isPending}>Add fan</Button>
        </>
      }
    >
      <div className={styles.grid2}>
        <Field label="First name" value={firstName} onChange={(e) => setFirstName(e.currentTarget.value)} />
        <Field label="Last name" value={lastName} onChange={(e) => setLastName(e.currentTarget.value)} />
      </div>
      <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
      <Field label="Phone" value={phone} onChange={(e) => setPhone(e.currentTarget.value)} />
      <div className={styles.grid2}>
        <Field label="City" value={city} onChange={(e) => setCity(e.currentTarget.value)} />
        <Field label="State" value={state} onChange={(e) => setState(e.currentTarget.value)} />
      </div>
      <MetroHintNote city={city} state={state} />
      <Field label="Country" value={country} onChange={(e) => setCountry(e.currentTarget.value)} hint="ISO code preferred (e.g. US)" />

      <label className={styles.check}>
        <input type="checkbox" checked={emailSubscribed} onChange={(e) => setEmailSubscribed(e.currentTarget.checked)} />
        Subscribed to email
      </label>
      <label className={styles.check}>
        <input type="checkbox" checked={smsOptIn} onChange={(e) => setSmsOptIn(e.currentTarget.checked)} />
        Opted in to SMS
      </label>

      {error && <p className={styles.formError} role="alert">{error}</p>}
    </FormDialog>
  );
}
